import { useObserve } from 'hooks';
import { useState, useEffect, useMemo } from 'react';
import { throttle } from 'lodash';

interface Props {
  ref: React.RefObject<HTMLElement>;
}

function useScroll({ ref }: Props) {
  const [scrollY, setScrollY] = useState<number>(0);
  const [scrollHeight, setScrollHeight] = useState<number>(0);
  const [scrollTop, setScrollTop] = useState<number>(0);
  const [clientHeight, setClientHeight] = useState<number>(0);

  const { height } = useObserve(ref);

  const listener = () => {
    if (!ref || !ref.current) {
      setScrollY(window.pageYOffset);
      return
    }

    setScrollHeight(ref.current.scrollHeight);
    setScrollTop(ref.current.scrollTop);
    setClientHeight(ref.current.clientHeight);
  };

  const throttledListener = useMemo(() => throttle(listener, 50), []);

  useEffect(() => {
    if (!ref || !ref.current) {
      window.addEventListener('scroll', throttledListener);
      return () => {
        window.removeEventListener('scroll', throttledListener);
      };
    }

    ref.current.addEventListener('scroll', throttledListener);
    return () => {
      if (ref && ref.current) {
        ref.current.removeEventListener('scroll', throttledListener);
      }
    };
  });

  useEffect(() => {
    throttledListener();
  }, [height]);

  return {
    scrollY,
    scrollHeight,
    scrollTop,
    clientHeight,
  };
}

export default useScroll;
